import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './MessageAssist.module.css';
import { ButtonData, ButtonContainer, ButtonInfo } from '../jsxElements/Button';
import { AmpFlagSet } from '../amplitude';
import { Card } from '../jsxElements/Card';
import faq from '../resources/intentFaq.json';
import messageAssistFaQTitleAndSubject from '../resources/messageAssistFaQTitleAndSubject.json';
import helpMeRequest from '../resources/helpMeRequest.json';
import { MESSAGE_ASSIST_MESSAGE_EVENT_TYPES } from '../constants';
import { postMessageAssistMessage } from '../messageassist';
import { logEvent, logException } from '../logging';
import { Activity } from '../types';
import { addAttributionToAnswer, renderAsMarkdown } from '../helpers';
import { sendExternalLinkSelected } from '../analytics';
import { isUnified } from '../helpers';
import { GraceFeatureFlags } from '../featureFlags';

export interface FaqData {
    title?: string;
    subject?: string;
    icon?: string;
    featureFlag?: string;
    requiresConversation?: boolean;
    showSurveyForCategory?: boolean;
    showSendMessageResponse?: boolean;
    autoRedirect?: boolean;
    reverseButtons?: boolean;
    isEmergency?: boolean;
    helpMeRequest?: string;
}

interface FaqDataMap {
    [key: string]: FaqData;
}

interface MATitleAndSubjectStrings {
    [key: string]: string;
}

interface MAHelpMeRequestStrings {
    [key: string]: string;
}

// TODO - resource handler to return brand specific assets.
const faqData: FaqDataMap = faq.providence;

const titleAndSubjectStrings: MATitleAndSubjectStrings =
    messageAssistFaQTitleAndSubject.default;

const helpMeRequestStrings: MAHelpMeRequestStrings = helpMeRequest.default;

export interface MessageAssistQnaData {
    qnaAnswer: string;
    qnaIntent: string;
    actions?: ButtonData[];
    subject?: string;
    showSurveyForCategory?: boolean;
    qnaSource?: string;
}

type MessageAssistQnaArgs = {
    data: MessageAssistQnaData;
    appendMessage?: (message: Activity) => void;
    sendPostBack: (message: string) => void;
};

/**
 * Simulate click event on an anchor tag.
 * @param anchorElement HTMLAnchorElement
 * @returns
 */
export function performAnchorLinkClickAction(anchorElement: HTMLAnchorElement) {
    // basic validatation of href before doing anything
    if (
        !anchorElement.href ||
        !(
            anchorElement.href.startsWith('https://') ||
            anchorElement.href.startsWith('tel:')
        )
    ) {
        return;
    }

    if (anchorElement.target === '_blank') {
        // open in new tab
        window.open(anchorElement.href, '_blank')?.focus();
        return;
    }

    // redirect.
    window.location.href = anchorElement.href;
}

export function replaceFaqKeysWithStrings(faqData: FaqData) {
    if (faqData?.subject) {
        faqData.subject =
            titleAndSubjectStrings[faqData.subject] ?? faqData.subject;
    }

    if (faqData?.title) {
        faqData.title = titleAndSubjectStrings[faqData.title] ?? faqData.title;
    }

    if (faqData?.helpMeRequest) {
        faqData.helpMeRequest =
            helpMeRequestStrings[faqData.helpMeRequest] ??
            faqData.helpMeRequest;
    }

    return faqData;
}

export function isIntentEnabledByFeatureFlag(
    data: FaqData,
    featureFlags: AmpFlagSet
): boolean {
    if (data?.featureFlag) {
        return !!featureFlags[data.featureFlag];
    }
    return true;
}

export function isCategoricalExperienceOffForIntent(
    data: FaqData,
    featureFlags?: AmpFlagSet
): boolean {
    return (
        !!featureFlags &&
        data?.featureFlag ==
            GraceFeatureFlags.MessageAssistCategoricalExperience &&
        !featureFlags[data.featureFlag]
    );
}

// Get data and replace subject and title keys with strings
export function getFaqDataForIntent(
    qnaIntent: string,
    featureFlags?: AmpFlagSet
): FaqData | undefined {
    if (!qnaIntent) {
        return undefined;
    }
    const domain = qnaIntent.split('.')[0];
    const data = faqData[qnaIntent] || faqData[domain];
    if (!data) {
        return undefined;
    }

    const isEnabled =
        !featureFlags || isIntentEnabledByFeatureFlag(data, featureFlags);
    const parsedContent = replaceFaqKeysWithStrings({ ...data });

    if (isCategoricalExperienceOffForIntent(data, featureFlags)) {
        // Defer to Rasa for send message flow intents
        return {
            ...parsedContent,
            requiresConversation: true,
        };
    }
    if (!isEnabled) {
        // Make sure no flow is shown to user if feature flag is disabled,
        // but preserve intent and subject.
        return {
            ...parsedContent,
            requiresConversation: false,
        };
    }

    return parsedContent;
}

export function shouldPresentSendMessageFallbackForIntent(
    qnaIntent: string
): boolean {
    if (!qnaIntent || !faqData || !faqData[qnaIntent]) {
        return false;
    }

    return !!faqData[qnaIntent].showSendMessageResponse;
}

export const MessageAssistQnaResponse: React.FC<MessageAssistQnaArgs> = ({
    data,
    appendMessage,
    sendPostBack,
}) => {
    const firstRender = useRef(true);
    const disableButtons = false;
    const featureFlags: AmpFlagSet = {};
    const faqData = getFaqDataForIntent(data.qnaIntent);
    const cardTitle = faqData?.title;
    const cardSubject = faqData?.subject || cardTitle;
    const reverseButtons = faqData?.reverseButtons || false;
    const [buttons, setButtons] = useState<ButtonInfo[]>([]);

    const { qnaAnswer, qnaIntent, qnaSource } = data;
    const answerWithAttribution = addAttributionToAnswer(
        qnaAnswer,
        qnaIntent,
        qnaSource
    );

    const handleOnClick = async (e: React.MouseEvent<HTMLElement>) => {
        const target: EventTarget = e.target;
        const anchorElement = target as HTMLAnchorElement;
        sendExternalLinkSelected(anchorElement.text, anchorElement.href);

        // Only post hyperlink click event to channel if on Prov app
        if (isUnified() && (target as HTMLAnchorElement).href) {
            try {
                await postMessageAssistMessage(
                    MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.DEEP_LINK,
                    anchorElement.innerHTML,
                    JSON.stringify({ link: anchorElement.href })
                );

                // Only stop event propagation if message posting is successful
                e.preventDefault();
                e.stopPropagation();
            } catch (error: any) {
                logException('Message Assist - Inline CTA handle error', {
                    exception: error,
                });

                // fall back to redirect behavior if postMessageAssistMessage fails
                performAnchorLinkClickAction(anchorElement);
            }
        }
    };

    useEffect(() => {
        if (!answerWithAttribution) {
            return;
        }

        // Log AppInsights event on first render of component
        if (firstRender.current) {
            firstRender.current = false;
            logEvent({
                name: 'BotMessageDisplayed',
                properties: {
                    title: cardTitle || '',
                    text: renderAsMarkdown(answerWithAttribution),
                },
            });
        }
    }, [answerWithAttribution]);

    useEffect(() => {
        const actions = data.actions ? [...data.actions] : [];
        if (reverseButtons) {
            actions.reverse();
        }
        setButtons(
            actions?.map((button, index) => ({
                button,
                autoFocus: index === 0,
                disableButtons,
                index,
                featureFlags,
                appendMessage,
                sendPostBack,
            })) || []
        );
    }, []);

    return (
        <Card
            title={cardTitle}
            icon={faqData?.icon ? '/' + faqData.icon : undefined}
        >
            <div
                onClick={handleOnClick}
                className={classNames(styles.messageAssistResponseBody)}
                dangerouslySetInnerHTML={{
                    __html: renderAsMarkdown(answerWithAttribution),
                }}
            />
            <ButtonContainer
                featureFlags={featureFlags}
                overrideContainerStyle={true}
                cardSubject={cardSubject}
                /**
                 * TODO: the below prop may need to be moved to
                 * <Card /> if Universal Card component is being
                 * reused and expects full width buttons. I'm
                 * unclear at this point if that design is tied
                 * to MA only or Universal Card
                 */
                isCardButton={true}
                buttons={buttons}
            />
        </Card>
    );
};
